import { useEffect, useState } from 'react'
import { AlertCircle, Radio } from 'lucide-react'
import { getCanaryTokens } from '../lib/api'
import { EmptyState } from './EmptyState'
import { cn, timeAgo, formatDate } from '../lib/utils'
import type { CanaryToken } from '../types'

// Read-only staff surface for deployed canary tokens (DW-FE-CANARY-ADMIN).
// Lists this tenant's canaries (GET /canary/tokens) with their fire counts so
// an analyst can see the tripwires without curl.
//
// Two honesty rules govern this panel:
//  - It renders ONLY an explicit allow-list of non-secret fields. The trigger
//    string/URL/key (`canary_token`/`canary_url`) is the capability secret a
//    forger would need — it is neither modelled on the CanaryToken type nor
//    read into the DOM here. We never map over arbitrary token keys.
//  - It makes no "protected"/"live-proven" claim. The panel reports what the
//    backend returns — how many times each token has fired — and nothing more.
//    A load failure surfaces honestly; nothing is faked or assumed calm.

// The explicit, non-secret projection rendered per row. Anything not named here
// — including any trigger secret that might ride along on the wire — is never
// read into the DOM.
function SafeRow({ token }: { token: CanaryToken }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 font-medium text-navy">{token.memo}</td>
      <td className="px-4 py-3 text-gray-600 uppercase text-xs tracking-wide">
        {token.token_type}
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            'px-2 py-0.5 rounded-full text-xs font-medium',
            token.is_active
              ? 'bg-success-light text-success'
              : 'bg-gray-100 text-gray-500',
          )}
        >
          {token.is_active ? 'Active' : 'Revoked'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            'font-semibold tabular-nums',
            token.triggered_count > 0 ? 'text-danger' : 'text-gray-400',
          )}
        >
          {token.triggered_count}
        </span>
      </td>
      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
        {token.last_triggered_at ? timeAgo(token.last_triggered_at) : 'never'}
      </td>
      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
        {formatDate(token.created_at)}
      </td>
    </tr>
  )
}

export function CanariesPanel() {
  const [tokens, setTokens] = useState<CanaryToken[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getCanaryTokens()
      .then((data) => setTokens(data))
      .catch(() => setLoadError('Could not load canary tokens.'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-navy">Canary tokens</h2>
        <p className="text-xs text-gray-500">
          Deployed tripwires for this tenant and how many times each has fired.
          Trigger URLs and keys are secrets and are never shown here; deploying
          and placing a canary is done outside this console.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-brass border-t-transparent rounded-full animate-spin" />
        </div>
      ) : loadError ? (
        <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span>{loadError} Nothing is shown because nothing could be loaded.</span>
        </div>
      ) : tokens.length === 0 ? (
        <EmptyState
          icon={Radio}
          title="No canary tokens"
          message="No canary tokens deployed for this tenant yet."
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Memo</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Fires</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Last fired</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Deployed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tokens.map((token) => (
                  <SafeRow key={token.id} token={token} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
